import React, { useEffect, useMemo, useState } from "react";
import RetentionLineChart from "./RetentionLineChart";
import RetentionCohortTable from "./RetentionCohortTable";
import RetentionLineTable from "./RetentionLineTable";
import {
  RETENTION_BREAKDOWN_CHART_TOP_DEFAULT,
  sliceBreakdownsForChart,
} from "./BreakdownTopValuesControl";
/**
 * RetentionVisualization - Main component for displaying retention data
 * Mirrors FunnelVisualization structure
 */
export default function RetentionVisualization({
  intervals = [],
  cohorts = [],
  weightedAverage = null,
  breakdowns = [],
  metric = "retention_rate",
  config = {},
  chartType = "retention-curve",
  startEventLabel = null,
  returnEventLabel = null,
  breakdownDimensionLabel = "",
  /** When true, only the line/curve chart is shown (e.g. board cards). */
  hideCohortTable = false,
  /** Pixel height for the line chart (board cards pass a constrained value). */
  chartHeight,
  /** Tighter spacing on boards */
  boardCompact = false,
  contentScale = 1,
}) {
  const retentionGroupIndex =
    typeof config?.retentionLineIntervalIndex === "number"
      ? config.retentionLineIntervalIndex
      : 0;
  const seriesLabel =
    (startEventLabel || returnEventLabel)
      ? `${startEventLabel || "Start Event"} then ${returnEventLabel || "Return Event"}`
      : "Retention";

  const hasBreakdowns = Array.isArray(breakdowns) && breakdowns.length > 0;
  const [breakdownChartTopN, setBreakdownChartTopN] = useState(RETENTION_BREAKDOWN_CHART_TOP_DEFAULT);

  useEffect(() => {
    setBreakdownChartTopN(RETENTION_BREAKDOWN_CHART_TOP_DEFAULT);
  }, [breakdownDimensionLabel]);

  const breakdownsForChart = useMemo(() => {
    if (!hasBreakdowns) return breakdowns;
    return sliceBreakdownsForChart(breakdowns, breakdownChartTopN);
  }, [breakdowns, breakdownChartTopN, hasBreakdowns]);

  const topValuesProps = hasBreakdowns
    ? { breakdownChartTopN, onBreakdownChartTopNChange: setBreakdownChartTopN }
    : {};

  const lineChartHeight =
    typeof chartHeight === "number" && chartHeight > 0 ? chartHeight : undefined;
  /** Board retention curve: grow chart to fill the card so flex-1 body does not leave a gap below. */
  const curveFillBoard =
    boardCompact && hideCohortTable && chartType === "retention-curve";
  const chartBlockMb = (() => {
    if (curveFillBoard) return "mb-0";
    if (boardCompact && chartType === "line") return "mb-1";
    if (boardCompact) return "mb-2";
    if (hideCohortTable && chartType === "retention-curve") return "mb-0";
    if (chartType === "retention-curve") return "mb-4";
    return "mb-6";
  })();
  const chartShellClass = curveFillBoard
    ? `${chartBlockMb} flex-1 min-h-0 w-full flex flex-col`
    : chartBlockMb;

  return (
    <div
      className={
        curveFillBoard ? "w-full min-h-0 flex-1 flex flex-col" : "w-full min-h-0"
      }
    >
      <div className={chartShellClass}>
        <RetentionLineChart
          intervals={intervals}
          cohorts={cohorts}
          weightedAverage={weightedAverage}
          breakdowns={breakdownsForChart}
          metric={metric}
          config={config}
          chartType={chartType}
          boardCompact={boardCompact}
          contentScale={contentScale}
          {...(curveFillBoard
            ? { height: "100%" }
            : lineChartHeight != null
              ? { height: lineChartHeight }
              : {})}
        />
      </div>

      {/* Line table (Line view only) */}
      {chartType === "line" && (
        <div className={boardCompact ? "mb-0" : "mb-6"}>
          <RetentionLineTable
            cohorts={cohorts}
            breakdowns={breakdowns}
            breakdownDimensionLabel={breakdownDimensionLabel}
            metric={metric}
            intervals={intervals}
            retentionGroupIndex={retentionGroupIndex}
            seriesLabel={seriesLabel}
            boardCompact={boardCompact}
            {...topValuesProps}
          />
        </div>
      )}

      {/* Cohort Table (only for Retention Curve; hidden on board) */}
      {chartType === "retention-curve" && !hideCohortTable && (
        <div className="pb-0 flex-shrink-0">
          <RetentionCohortTable
            intervals={intervals}
            cohorts={cohorts}
            weightedAverage={weightedAverage}
            breakdowns={breakdowns}
            breakdownDimensionLabel={breakdownDimensionLabel}
            metric={metric}
            config={config}
            {...topValuesProps}
          />
        </div>
      )}
    </div>
  );
}
